import React from "react"
import { Link } from "react-router";
import { useParams } from "react-router";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { useForm } from "react-hook-form";

import Button from '../../../shared/ui/Button';
import Modal from '../../../shared/ui/Modal';

import { useAuth } from "../../../features/auth/AuthContext";
import { fetchShop, createShop, updateShop, deleteShop } from "../shops.api";


const ShopEditPage: React.FC = () => {
    // useAuth
    const auth = useAuth();
    const params = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const shopListPath = searchParams.get('return') === 'settings-shops'
        ? "/userconfig/?tab=shops"
        : "/shops/";

    // useState
    const [item, setItem] = React.useState<boolean>(false);
    const [accessKeyLabel, setApiAccessKeyLabel] = React.useState<string>("API 액세스키");
    const [secretKeyLabel, setApiSecretKeyLabel] = React.useState<string>("API 시크릿키");
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [processMessage, setProcessMessage] = React.useState<string | null>(null);
    const [modalDeleteShow, setModalDeleteShow] = React.useState<boolean>(false)

    // hookForm
    const { register, handleSubmit, reset, setError, clearErrors, setValue, watch, formState: { errors } } = useForm<ShopFormInputs>({
        defaultValues: {
            market_site: "",
            name: "",
            api_key: "",
            api_secret: "",
            seller_center_id: "",
            seller_center_pw: "",
            url: "",
        }
    });
    const watchedMarketSite = watch('market_site');

    const [marketSite, setMarketSite] = React.useState<string>("");
    const prevMarketSiteRef = React.useRef<string>("");

    // useEffects
    React.useEffect(() => {
        const setResult = (data: any) => {
            prevMarketSiteRef.current = data.market_site || "";
            reset(data);
            setItem(true);
        }
        if (params.id) {
            fetchShop({ id: params.id, auth, setErrorMessage: setErrorMessage, setResult })
        }
    }, [reset, params.id]);

    React.useEffect(() => {
        setMarketSite(watchedMarketSite || 'smartstore');
    }, [watchedMarketSite]);

    React.useEffect(() => {
        const selectedMarketSite = marketSite || 'smartstore';
        const prevMarketSite = prevMarketSiteRef.current;

        if (selectedMarketSite === 'smartstore') {
            setApiAccessKeyLabel("액세스키");
            setApiSecretKeyLabel("시크릿키");
        } else if (selectedMarketSite === 'auction' || selectedMarketSite === 'gmarket') {
            setApiAccessKeyLabel("상점ID");
            setApiSecretKeyLabel("시크릿키");
        } else if (selectedMarketSite === 'lotteon' || selectedMarketSite === 'ssgdotcom') {
            setApiAccessKeyLabel("오픈API키");
            setApiSecretKeyLabel("시크릿키");
        } else {
            setApiAccessKeyLabel("사용자ID");
            setApiSecretKeyLabel("오픈API키");
        }

        if (prevMarketSite && marketSite && prevMarketSite !== marketSite) {
            setValue('api_key', '');
            setValue('api_secret', '');
            setValue('url', '');
            setValue('seller_center_id', '');
            setValue('seller_center_pw', '');
            clearErrors(['api_key', 'api_secret', 'url', 'seller_center_id', 'seller_center_pw']);
        }

        prevMarketSiteRef.current = marketSite || "";
    }, [marketSite, clearErrors, setValue]);

    // handlers
    const handleModalDeleteOpen = () => setModalDeleteShow(true)
    const handleModalDeleteClose = () => setModalDeleteShow(false)
    const onSubmit = async (data: ShopFormInputs) => {
        const isEsmMarket = data.market_site === 'auction' || data.market_site === 'gmarket';
        const isEleventhMarket = data.market_site === 'eleventh';
        let payload = {
            market_site: data.market_site,
            name: data.name,
            api_key: isEleventhMarket ? '' : data.api_key,
            api_secret: isEsmMarket || (data.market_site === 'lotteon' || data.market_site === 'ssgdotcom') ? '' : data.api_secret,
            seller_center_id: data.market_site === 'smartstore' || data.market_site === 'lotteon' ? data.seller_center_id : '',
            seller_center_pw: data.market_site === 'smartstore' || data.market_site === 'lotteon' ? data.seller_center_pw : '',
            url: data.url,
        }

        // 프로세스 메시지 설정
        setProcessMessage("API 접속을 테스트하고 있습니다...");

        if (params.id && item) {
            await updateShop({
                id: params.id,
                auth,
                payload,
                setErrorMessage,
                setProcessMessage,
                setResult: () => { navigate(shopListPath) },
                setError,
            })
        } else if (!params.id) {
            await createShop({
                auth: auth,
                payload: payload,
                setErrorMessage,
                setProcessMessage,
                setResult: () => { navigate(shopListPath) },
                setError,
            })
        }
    }

    const handleDelete = async () => {
        (params.id && item) && await deleteShop({
            id: params.id,
            auth,
            setErrorMessage,
            setProcessMessage,
            setResult: () => { navigate(shopListPath) }
        });
        setModalDeleteShow(false);
    }

    return (
        <>
            {errorMessage && (
                <div className="alert alert-danger" role="alert">
                    {errorMessage}
                </div>
            )}
            {processMessage && (
                <div className="alert alert-info" role="alert">
                    {processMessage}
                </div>
            )}
            <div className="page-head d-flex justify-content-between align-items-center mb-0">
                <div className="pt-3">
                    <p className="fs-5">
                        상점등록
                    </p>
                </div>
                <div>
                    {item && (
                        <>
                            <button type="button" className="btn btn-danger" onClick={handleModalDeleteOpen}>
                                삭제
                            </button>
                            {/* <DeleteConfirmModal onDelete={handleDelete} /> */}
                        </>
                    )}
                    <Link to={shopListPath} className="btn btn-outline-dark ms-2">뒤로 가기</Link>
                </div>
            </div>

            <hr className="my-0 mb-3" />
            {
                // update mode and item is loading
                (params.id && !item)
                &&
                <form>
                    <ul className="list-unstyled"><li className="mb-3"></li></ul>
                </form>
            }
            {
                // update mode and item loaded
                (params.id && item)
                &&
                <ShopForm {...{
                    register, handleSubmit, onSubmit, formState: { errors },
                    accessKeyLabel, secretKeyLabel, marketSite,
                    isEsmMarket: marketSite === 'auction' || marketSite === 'gmarket',
                    isEleventhMarket: marketSite === 'eleventh',
                    isLotteonMarket: marketSite === 'lotteon',
                }} />
            }
            {   // create mode
                !params.id
                &&
                <ShopForm {...{
                    register, handleSubmit, onSubmit, formState: { errors },
                    accessKeyLabel, secretKeyLabel, marketSite,
                    isEsmMarket: marketSite === 'auction' || marketSite === 'gmarket',
                    isEleventhMarket: marketSite === 'eleventh',
                    isLotteonMarket: marketSite === 'lotteon',
                }} />
            }

            <Modal show={modalDeleteShow} onHide={handleModalDeleteClose}>
                <Modal.Header closeButton>
                    <Modal.Title>확인</Modal.Title>
                </Modal.Header>
                <Modal.Body>삭제하시겠습니까?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalDeleteClose}>취소</Button>
                    <Button variant="danger" onClick={handleDelete}>삭제</Button>
                </Modal.Footer>
            </Modal>

        </>
    );
}

interface ShopFormInputs {
    market_site: string;
    name: string;
    api_key: string;
    api_secret: string;
    seller_center_id: string;
    seller_center_pw: string;
    url?: string;
}

const ShopForm = (props: any) => {

    const { register, handleSubmit, onSubmit, formState: { errors } } = props;
    const { accessKeyLabel, secretKeyLabel, isEsmMarket, isEleventhMarket, isLotteonMarket, marketSite } = props;

    return <form className="form-card form-card-narrow" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ul className="mb-3 list-unstyled">
            <li className="mb-3">
                <label htmlFor="id_site" className="form-label">마켓 <span className="text-danger">*</span></label>
                <select
                    id="id_site"
                    className={`form-select ${errors.market_site ? "is-invalid" : ""}`}
                    {...register("market_site", { required: "마켓을 선택하세요." })}>
                    <option value="">-- 선택 --</option>
                    <option value="smartstore">스마트스토어</option>
                    <option value="auction">옥션</option>
                    <option value="gmarket">지마켓</option>
                    <option value="eleventh">11번가</option>
                    <option value="lotteon">롯데ON</option>
                    <option value="ssgdotcom">SSG닷컴</option>
                </select>
                {errors.market_site && <div className="invalid-feedback">{errors.market_site.message}</div>}
            </li>
            <li className="mb-3">
                <label htmlFor="id_name" className="form-label">상점명 <span className="text-danger">*</span></label>
                <input
                    type="text"
                    id="id_name"
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    {...register("name", { required: "상점명을 입력하세요.", minLength: 1 })}
                />
                {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
            </li>
            {!isEleventhMarket && (
                <li className="mb-3">
                    <label htmlFor="id_api_key" className="form-label">
                        {accessKeyLabel} <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        id="id_api_key"
                        className={`form-control ${errors.api_key ? "is-invalid" : ""}`}
                        {...register("api_key", {
                            required: `${accessKeyLabel}를 입력하세요.`,
                            minLength: { value: 4, message: `${accessKeyLabel}는 4자 이상 입력하세요.` },
                        })}
                    />
                    {errors.api_key && <div className="invalid-feedback">{errors.api_key.message}</div>}
                </li>
            )}
            {!isEsmMarket && !isLotteonMarket && marketSite !== 'ssgdotcom' && (
                <li className="mb-3">
                    <label htmlFor="id_api_secret" className="form-label">
                        {secretKeyLabel} <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        id="id_api_secret"
                        className={`form-control ${errors.api_secret ? "is-invalid" : ""}`}
                        {...register("api_secret", {
                            required: `${secretKeyLabel}를 입력하세요.`,
                            minLength: { value: 4, message: `${secretKeyLabel}는 4자 이상 입력하세요.` },
                        })}
                    />
                    {errors.api_secret && <div className="invalid-feedback">{errors.api_secret.message}</div>}
                </li>
            )}
            {(marketSite === 'smartstore' || marketSite === 'lotteon') && (
                <>
                    <li className="mb-3">
                        <label htmlFor="id_seller_center_id" className="form-label">판매자센터ID {marketSite === 'smartstore' && <span className="text-danger">*</span>}</label>
                        <input
                            type="text"
                            id="id_seller_center_id"
                            className={`form-control ${errors.seller_center_id ? "is-invalid" : ""}`}
                            {...register("seller_center_id", { required: marketSite === 'smartstore' && "판매자센터ID를 입력하세요." })}
                        />
                        {errors.seller_center_id && <div className="invalid-feedback">{errors.seller_center_id.message}</div>}
                    </li>
                    <li className="mb-3">
                        <label htmlFor="id_seller_center_pw" className="form-label">판매자센터PW {marketSite === 'smartstore' && <span className="text-danger">*</span>}</label>
                        <input
                            type="password"
                            id="id_seller_center_pw"
                            className={`form-control ${errors.seller_center_pw ? "is-invalid" : ""}`}
                            {...register("seller_center_pw", { required: marketSite === 'smartstore' && "판매자센터PW를 입력하세요." })}
                        />
                        {errors.seller_center_pw && <div className="invalid-feedback">{errors.seller_center_pw.message}</div>}
                    </li>
                </>
            )}
            <li className="mb-3">
                <label htmlFor="id_url" className="form-label">
                    URL (선택)
                </label>
                <input
                    type="text"
                    id="id_url"
                    className={`form-control ${errors.url ? "is-invalid" : ""}`}
                    {...register("url", {
                        validate: (value: string) => !value || value.length >= 4 || "URL은 4자 이상 입력하세요.",
                    })}
                />
                {errors.url && <div className="invalid-feedback">{errors.url.message}</div>}
            </li>
            <li className="mb-3">
                <button type="submit" className="btn btn-primary w-100">저장</button>
            </li>
        </ul>
    </form>
}


export default ShopEditPage;
